import { TextInput } from '@ipfabric/ui-components/TextInput/TextInput';
import { withTargetValue } from '@ipfabric/ui-components/utils/events/withTargetValue';
import {
  ENTER,
  ESCAPE,
} from '@ipfabric/ui-components/utils/keyboard/constants';
import { DropdownMenu } from 'components/DropdownMenu/DropdownMenu';
import { getFlatStringFilterValue } from 'components/Table/components/SimpleFilter/helpers/getFlatStringFilterValue';
import { useRef, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';
import {
  getAllowedOperators,
  operatorReadableMap,
} from './helpers/filterOperators';
import { getFilterFromReadable } from './helpers/getFilterFromReadable';
import type { ChangeFilterEvent } from './types/ChangeFilterEvent';
import type { SimpleFilterField } from './types/SimpleFilterField';

interface Props {
  filterField: SimpleFilterField;
  onChangeFilter: (command: ChangeFilterEvent) => void;
  onSubmitFilters: () => void;
  autoFocus?: boolean;
}

export const SimpleTextFilter: FC<Props> = (props) => {
  const [focused, setFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const inputWidth = inputWrapperRef?.current?.clientWidth;

  const handleChange = (value: string) => {
    props.onChangeFilter({
      column: props.filterField.column,
      value: value ?? null,
    });
  };

  const setFocusOut = () => {
    inputRef.current?.blur();
    setFocused(false);
  };

  const clear = async () => {
    await props.onChangeFilter({
      column: props.filterField.column,
      value: null,
    });

    props.onSubmitFilters();

    setFocusOut();
  };

  const handleInputKeyDown = (e: KeyboardEvent) => {
    if (e.keyCode === ESCAPE) {
      clear();
    }
    if (e.keyCode === ENTER) {
      props.onSubmitFilters();
      setFocusOut();
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const setOperator = (operator: string) => {
    const filterValue = getFilterFromReadable(
      props.filterField.filter,
      props.filterField.value,
    );

    handleChange(filterValue ? `${operator}${filterValue[1]}` : operator);
  };

  const operatorItems = getAllowedOperators(props.filterField.filter).map(
    (operator) => ({
      label: operatorReadableMap[operator],
      onClick: () => {
        setOperator(operatorReadableMap[operator]);
      },
    }),
  );

  return (
    <div className='flex-1' onBlur={handleBlur} ref={inputWrapperRef}>
      <TextInput
        type='search'
        value={getFlatStringFilterValue(props.filterField.value)}
        onChange={withTargetValue(handleChange)}
        onFocus={handleFocus}
        inputRef={inputRef}
        size='small'
        hasError={props.filterField.error}
        onClear={clear}
        onKeyDown={handleInputKeyDown}
        autoFocus={props.autoFocus}
      />
      {focused && operatorItems.length !== 0 && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          <DropdownMenu
            onClose={() => {
              inputRef.current?.focus();
            }}
            style={{ minWidth: inputWidth ? `${inputWidth}px` : 'auto' }}
            items={operatorItems}
          />
        </div>
      )}
    </div>
  );
};
